import React, { useState, useEffect } from 'react';
import { 
  GitBranch, 
  Plus, 
  Save, 
  X, 
  ChevronRight,
  Settings,
  Users,
  Bell,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit,
  Trash2,
  Copy,
  Eye,
  Send
} from 'lucide-react';
import { WorkflowService } from '../services/WorkflowService';
import WorkflowAssignment from './WorkflowAssignment';
import WorkflowAnalytics from './WorkflowAnalytics';

const WorkflowDesigner = ({ userProfile }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('templates');
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Form state for new/edit template
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    type: 'special_project',
    stages: [],
    rules: {
      timeLimit: 48, // hours
      escalation: true,
      parallelApprovals: false
    },
    fields: [],
    notifications: {
      onCreate: true,
      onApprove: true,
      onReject: true,
      onComplete: true
    }
  });

  // Predefined workflow types
  const workflowTypes = [
    { value: 'special_project', label: 'Special Project Request', icon: '🚀' },
    { value: 'purchase_order', label: 'Purchase Order', icon: '🛒' },
    { value: 'time_off', label: 'Time Off Request', icon: '🏖️' },
    { value: 'expense_claim', label: 'Expense Claim', icon: '💰' },
    { value: 'custom', label: 'Custom Workflow', icon: '⚙️' }
  ];

  // Stage types
  const stageTypes = [
    { value: 'approval', label: 'Approval', icon: CheckCircle, color: 'text-green-600' },
    { value: 'review', label: 'Review', icon: Eye, color: 'text-blue-600' },
    { value: 'execute', label: 'Execute', icon: Settings, color: 'text-purple-600' },
    { value: 'notify', label: 'Notify', icon: Bell, color: 'text-yellow-600' },
    { value: 'conditional', label: 'Conditional', icon: GitBranch, color: 'text-orange-600' }
  ];

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await WorkflowService.getWorkflowTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTemplate = (template) => {
    setTemplateForm({
      name: template.name || '',
      description: template.description || '',
      type: template.type || 'special_project',
      stages: template.stages || [],
      rules: template.rules || {
        timeLimit: 48,
        escalation: true,
        parallelApprovals: false
      },
      fields: template.fields || [],
      notifications: template.notifications || {
        onCreate: true,
        onApprove: true,
        onReject: true,
        onComplete: true
      }
    });
    setEditingTemplate(template);
    setIsCreating(true);
  };

  const handleCreateTemplate = () => {
    setIsCreating(true);
    setSelectedTemplate(null);
    setEditingTemplate(null);
    // Initialize with a basic Special Project template
    setTemplateForm({
      name: 'Special Project Request',
      description: 'Request approval for special projects and initiatives',
      type: 'special_project',
      stages: [
        {
          id: 'stage_1',
          name: 'Initial Review',
          type: 'approval',
          description: 'Manager reviews and approves the request',
          assignTo: 'manager',
          actions: ['approve', 'reject', 'request_info'],
          timeLimit: 48,
          rules: {}
        },
        {
          id: 'stage_2',
          name: 'Execution',
          type: 'execute',
          description: 'Assigned team member executes the project',
          assignTo: 'assignee',
          actions: ['complete', 'escalate'],
          timeLimit: 0,
          rules: {}
        },
        {
          id: 'stage_3',
          name: 'Completion',
          type: 'notify',
          description: 'Notify requester of completion',
          assignTo: 'requester',
          actions: ['acknowledge'],
          timeLimit: 0,
          rules: {}
        }
      ],
      rules: {
        timeLimit: 48,
        escalation: true,
        parallelApprovals: false,
        conditionalRouting: []
      },
      fields: [
        { id: 'title', name: 'Project Title', type: 'text', required: true },
        { id: 'description', name: 'Description', type: 'textarea', required: true },
        { id: 'budget', name: 'Estimated Budget', type: 'number', required: false },
        { id: 'deadline', name: 'Deadline', type: 'date', required: false },
        { id: 'priority', name: 'Priority', type: 'select', required: true, 
          options: ['Low', 'Medium', 'High', 'Critical'] },
        { id: 'attachments', name: 'Attachments', type: 'file', required: false }
      ],
      notifications: {
        onCreate: true,
        onApprove: true,
        onReject: true,
        onComplete: true,
        onEscalate: true
      }
    });
  };

  const handleSaveTemplate = async () => {
    try {
      setLoading(true);
      
      let result;
      if (editingTemplate) {
        // Update existing template
        result = await WorkflowService.updateWorkflowTemplate(editingTemplate.id, {
          ...templateForm,
          updatedBy: userProfile.uid
        });
      } else {
        // Create new template
        result = await WorkflowService.createWorkflowTemplate({
          ...templateForm,
          createdBy: userProfile.uid
        });
      }
      
      if (result.success) {
        await loadTemplates();
        setIsCreating(false);
        setEditingTemplate(null);
        setTemplateForm({
          name: '',
          description: '',
          type: 'special_project',
          stages: [],
          rules: {},
          fields: [],
          notifications: {}
        });
      }
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setLoading(false);
    }
  };

  const addStage = () => {
    // Calculate the next stage number based on existing stages
    const stageNumbers = templateForm.stages
      .map(s => {
        const match = s.id.match(/^stage_(\d+)$/);
        return match ? parseInt(match[1]) : 0;
      })
      .filter(n => n > 0);
    
    const nextStageNumber = stageNumbers.length > 0 
      ? Math.max(...stageNumbers) + 1 
      : templateForm.stages.length + 1;
    
    const newStage = {
      id: `stage_${nextStageNumber}`,
      name: `Stage ${nextStageNumber}`,
      type: 'approval',
      description: '',
      assignTo: '',
      actions: ['approve', 'reject'],
      timeLimit: 48,
      rules: {}
    };
    
    setTemplateForm(prev => ({
      ...prev,
      stages: [...prev.stages, newStage]
    }));
  };

  const updateStage = (stageId, updates) => {
    setTemplateForm(prev => ({
      ...prev,
      stages: prev.stages.map(stage => 
        stage.id === stageId ? { ...stage, ...updates } : stage
      )
    }));
  };

  const removeStage = (stageId) => {
    setTemplateForm(prev => ({
      ...prev,
      stages: prev.stages.filter(stage => stage.id !== stageId)
    }));
  };

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      name: 'New Field',
      type: 'text',
      required: false
    };
    
    setTemplateForm(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  const updateField = (fieldId, updates) => {
    setTemplateForm(prev => ({
      ...prev,
      fields: prev.fields.map(field => 
        field.id === fieldId ? { ...field, ...updates } : field
      )
    }));
  };

  const removeField = (fieldId) => {
    setTemplateForm(prev => ({
      ...prev,
      fields: prev.fields.filter(field => field.id !== fieldId)
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GitBranch className="h-6 w-6 text-blue-600" />
            Workflow Designer
          </h2>
          <p className="text-gray-600 mt-1">
            Create and manage workflow templates for your tenants
          </p>
        </div>
        
        {!isCreating && (
          <button
            onClick={handleCreateTemplate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={16} />
            Create Template
          </button>
        )}
      </div>

      {/* Tabs */}
      {!isCreating && (
        <div className="flex gap-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('templates')}
            className={`pb-2 px-1 border-b-2 transition-colors ${
              activeTab === 'templates' 
                ? 'border-blue-600 text-blue-600' 
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Templates
          </button>
          <button
            onClick={() => setActiveTab('assignments')}
            className={`pb-2 px-1 border-b-2 transition-colors ${
              activeTab === 'assignments' 
                ? 'border-blue-600 text-blue-600' 
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Assignments
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`pb-2 px-1 border-b-2 transition-colors ${
              activeTab === 'analytics' 
                ? 'border-blue-600 text-blue-600' 
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Analytics
          </button>
        </div>
      )}

      {/* Content */}
      {isCreating ? (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold">
              {editingTemplate ? 'Edit Workflow Template' : 'Create Workflow Template'}
            </h3>
            <button
              onClick={() => setIsCreating(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>

          {/* Template Basic Info */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name
              </label>
              <input
                type="text"
                value={templateForm.name}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Special Project Request"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={templateForm.description}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Describe what this workflow is for..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Workflow Type
              </label>
              <select
                value={templateForm.type}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {workflowTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Workflow Stages */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-md font-semibold text-gray-900">Workflow Stages</h4>
              <button
                onClick={addStage}
                className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
              >
                <Plus size={14} />
                Add Stage
              </button>
            </div>

            <div className="space-y-3">
              {templateForm.stages.map((stage, index) => (
                <div key={stage.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">Stage {index + 1}</span>
                      {stageTypes.find(t => t.value === stage.type) && (
                        <span className={stageTypes.find(t => t.value === stage.type).color}>
                          {React.createElement(stageTypes.find(t => t.value === stage.type).icon, { size: 16 })}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => removeStage(stage.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Stage Name
                      </label>
                      <input
                        type="text"
                        value={stage.name}
                        onChange={(e) => updateStage(stage.id, { name: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Stage Type
                      </label>
                      <select
                        value={stage.type}
                        onChange={(e) => updateStage(stage.id, { type: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      >
                        {stageTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={stage.description}
                        onChange={(e) => updateStage(stage.id, { description: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="What happens in this stage..."
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Assign To
                      </label>
                      <select
                        value={stage.assignTo}
                        onChange={(e) => updateStage(stage.id, { assignTo: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      >
                        <option value="">Select...</option>
                        <option value="requester">Requester</option>
                        <option value="manager">Manager</option>
                        <option value="specific">Specific User</option>
                        <option value="group">Group</option>
                        <option value="assignee">Assignee</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Time Limit (hours)
                      </label>
                      <input
                        type="number"
                        value={stage.timeLimit}
                        onChange={(e) => updateStage(stage.id, { timeLimit: parseInt(e.target.value) })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="0 for no limit"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form Fields */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-md font-semibold text-gray-900">Form Fields</h4>
              <button
                onClick={addField}
                className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
              >
                <Plus size={14} />
                Add Field
              </button>
            </div>

            <div className="space-y-2">
              {templateForm.fields.map(field => (
                <div key={field.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="text"
                    value={field.name}
                    onChange={(e) => updateField(field.id, { name: e.target.value })}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                    placeholder="Field name"
                  />
                  <select
                    value={field.type}
                    onChange={(e) => updateField(field.id, { type: e.target.value })}
                    className="px-2 py-1 text-sm border border-gray-300 rounded"
                  >
                    <option value="text">Text</option>
                    <option value="textarea">Textarea</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="select">Select</option>
                    <option value="file">File</option>
                  </select>
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) => updateField(field.id, { required: e.target.checked })}
                      className="rounded"
                    />
                    Required
                  </label>
                  <button
                    onClick={() => removeField(field.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Rules and Notifications */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">Rules</h4>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={templateForm.rules.escalation}
                    onChange={(e) => setTemplateForm(prev => ({
                      ...prev,
                      rules: { ...prev.rules, escalation: e.target.checked }
                    }))}
                    className="rounded"
                  />
                  Enable escalation for timeouts
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={templateForm.rules.parallelApprovals}
                    onChange={(e) => setTemplateForm(prev => ({
                      ...prev,
                      rules: { ...prev.rules, parallelApprovals: e.target.checked }
                    }))}
                    className="rounded"
                  />
                  Allow parallel approvals
                </label>
              </div>
            </div>

            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">Notifications</h4>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={templateForm.notifications.onCreate}
                    onChange={(e) => setTemplateForm(prev => ({
                      ...prev,
                      notifications: { ...prev.notifications, onCreate: e.target.checked }
                    }))}
                    className="rounded"
                  />
                  On creation
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={templateForm.notifications.onApprove}
                    onChange={(e) => setTemplateForm(prev => ({
                      ...prev,
                      notifications: { ...prev.notifications, onApprove: e.target.checked }
                    }))}
                    className="rounded"
                  />
                  On approval
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={templateForm.notifications.onReject}
                    onChange={(e) => setTemplateForm(prev => ({
                      ...prev,
                      notifications: { ...prev.notifications, onReject: e.target.checked }
                    }))}
                    className="rounded"
                  />
                  On rejection
                </label>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsCreating(false)}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveTemplate}
              disabled={loading || !templateForm.name}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save size={16} />
              {editingTemplate ? 'Update' : 'Save'} Template
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200">
          {activeTab === 'templates' && (
            <div className="p-6">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8">
                  <GitBranch className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900">No templates yet</h3>
                  <p className="text-gray-600 mt-1">Create your first workflow template to get started</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {templates.map(template => (
                    <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-lg font-medium text-gray-900">{template.name}</h4>
                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                              {template.type}
                            </span>
                          </div>
                          <p className="text-gray-600 mt-1">{template.description}</p>
                          <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                            <span>{template.stages?.length || 0} stages</span>
                            <span>{template.fields?.length || 0} fields</span>
                            <span>v{template.version || 1}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => handleEditTemplate(template)}
                            className="p-2 text-gray-400 hover:text-blue-600"
                            title="Edit Template"
                          >
                            <Edit size={16} />
                          </button>
                          <button className="p-2 text-gray-400 hover:text-green-600">
                            <Copy size={16} />
                          </button>
                          <button className="p-2 text-gray-400 hover:text-purple-600">
                            <Send size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'assignments' && (
            <WorkflowAssignment userProfile={userProfile} />
          )}

          {activeTab === 'analytics' && (
            <WorkflowAnalytics userProfile={userProfile} />
          )}
        </div>
      )}
    </div>
  );
};

export default WorkflowDesigner;